<template>
  <div v-if="isVisible" class="mobile-splash-screen" :class="{ 'fade-out': isFadingOut }">
    <video
      ref="videoRef"
      class="splash-video"
      :class="{ 'video-ready': isVideoPlaying }"
      src="/video/splash-video.mp4"
      autoplay
      muted
      playsinline
      preload="auto"
      disablePictureInPicture
      controlsList="nodownload nofullscreen noremoteplayback"
      @ended="onVideoEnded"
      @error="onVideoError"
      @playing="onVideoPlaying"
    ></video>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useSplashScreen } from '@/composables/useSplashScreen'

export default {
  name: 'MobileSplashScreen',
  emits: ['splash-finished'],
  setup(props, { emit }) {
    const isVisible = ref(true)
    const isFadingOut = ref(false)
    const isVideoPlaying = ref(false)
    const videoRef = ref(null)
    const { hideSplash } = useSplashScreen()
    let failsafeTimeout = null
    let hasReleasedNative = false

    const finishSplash = () => {
      if (isFadingOut.value || !isVisible.value) return
      isFadingOut.value = true
      
      // Tunggu animasi CSS fade-out selesai sebelum menghapus komponen (500ms)
      setTimeout(() => {
        isVisible.value = false
        if (failsafeTimeout) clearTimeout(failsafeTimeout)
        emit('splash-finished')
      }, 500)
    }

    const onVideoPlaying = () => {
      // Event playing jauh lebih aman dari canplay karena benar-benar menandakan frame pertama sudah digambar
      isVideoPlaying.value = true
      
      if (!hasReleasedNative) {
        // Fade out native splash Capacitor perlahan agar bergabung dengan layar putih Vue
        hideSplash(500)
        hasReleasedNative = true
      }
    }

    const onVideoEnded = () => {
      finishSplash()
    }

    const onVideoError = (e) => {
      console.warn('[MobileSplashScreen] Video failed to load or play, skipping video splash.', e)
      if (!hasReleasedNative) {
        hideSplash(100)
        hasReleasedNative = true
      }
      finishSplash()
    }

    onMounted(() => {
      // Failsafe: if video is too long or stuck, close it after 5 seconds
      failsafeTimeout = setTimeout(() => {
        console.warn('[MobileSplashScreen] Video timeout failsafe triggered')
        if (!hasReleasedNative) {
          hideSplash(100)
          hasReleasedNative = true
        }
        finishSplash()
      }, 5000)
    })

    return {
      isVisible,
      isFadingOut,
      isVideoPlaying,
      videoRef,
      onVideoEnded,
      onVideoError,
      onVideoPlaying
    }
  }
}
</script>

<style lang="scss" scoped>
.mobile-splash-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999999;
  background-color: #ffffff; /* Paksa warna putih agar transisi fade mulus */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.5s ease-in-out;
}

.mobile-splash-screen.fade-out {
  opacity: 0;
  pointer-events: none;
}

.splash-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0; /* Mulai tersembunyi untuk mencegat placeholder WebView Chrome */
  transition: opacity 0.5s ease-in-out;
}

.splash-video.video-ready {
  opacity: 1; /* Tampilkan perlahan setelah frame pertama videonya jalan */
}

/* 
 * HIDE NATIVE ANDROID/CHROME VIDEO PLAY BUTTON PLACEHOLDER 
 * Secara default, webview Android akan menggambar icon play segitiga besar 
 * selama sepersekian detik sebelum autoplay aktif. Code ini mematikannya.
 */
video::-webkit-media-controls,
video::-webkit-media-controls-start-playback-button,
video::-webkit-media-controls-play-button,
video::-webkit-media-controls-panel {
  display: none !important;
  opacity: 0 !important;
  -webkit-appearance: none !important;
}
</style>
